<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'HouseUpdateModal',
})
</script>
<template>
  <Modal v-model:visible="visible" centered destroy-on-close :title="title" width="600px" :confirm-loading="submitLoading" @ok="submit">
    <div class="ant-modal-bd">
      <Form ref="formRef" layout="vertical" label-align="left" :model="model" scroll-to-first-error class="ant-edit-form">
        <Row :gutter="16">
          <Col :span="24" v-if="!specified">
            <FormItem label="所属小区" name="zhengbuCommunityId" :rules="getRequiredRule('所属小区')">
              <CommonSelect
                :value="model.zhengbuCommunityId"
                :api="API_COMMUNITY_ALL"
                optionFilterProp="label"
                showSearch
                allowClear
                labelField="communityName"
                valueField="id"
                placeholder="请选择"
                @update:value="setCommunityId"
              />
            </FormItem>
          </Col>
          <Col :span="24">
            <FormItem
              label="房号"
              name="houseNumber"
              :rules="[getRequiredRule('房号'), { pattern: ROOM_NO_REG, message: '房号格式不正确，请按“楼栋-单元-楼层-房号”格式填写' }]"
              :label-help="ROOM_NO_HELP"
            >
              <Input v-model:value="model.houseNumber" placeholder="楼栋-单元-楼层-房号" allow-clear style="width: 100%" />
            </FormItem>
          </Col>
          <Col :span="12" v-if="model.zhengbuCommunityId">
            <FormItem label="户型" name="zhengbuCommunityTypeId" :rules="getRequiredRule('户型')">
              <CommonSelect
                v-model:value="model.zhengbuCommunityTypeId"
                :api="fetchHouseType"
                showSearch
                allowClear
                optionFilterProp="label"
                labelField="houseTypeName"
                valueField="id"
                placeholder="请选择"
              />
            </FormItem>
          </Col>
          <Col :span="12">
            <FormItem label="房屋性质" name="houseNatureType" :rules="getRequiredRule('房屋性质')">
              <Select
                v-model:value="model.houseNatureType"
                showSearch
                optionFilterProp="label"
                placeholder="请选择"
                :options="houseNatureTypeOptions"
              />
            </FormItem>
          </Col>
          <Col :span="24">
            <FormItem label="附件" name="houseExtraFiles">
              <FileUpload v-model:value="model.houseExtraFiles" :files-col="{ span: 24 }" />
            </FormItem>
          </Col>
        </Row>
      </Form>
    </div>
  </Modal>
</template>

<script lang="ts" setup name="HouseUpdateModal">
import { Modal, Form, Input, Row, Col, Select } from 'ant-design-vue'
import { computed, ref } from 'vue'
import { FormItem } from '-/Common/Form'
import { FileUpload } from '-/Common/FileUpload'
import { Select as CommonSelect } from '-/Common/Select'
import { getRequiredRule, ROOM_NO_HELP, ROOM_NO_REG } from '@/constant/form'
import { handleFormError } from '@/hooks/useForm'
import { API_HOUSE_ADD, API_HOUSE_EDIT } from '@/api/house'
import { useDictStore } from '@/store/modules/dict'
import { genSimpleConditionItem } from '@/hooks/useConditions'
import { API_HOUSE_TYPE_ALL } from '@/api/houseType'
import { API_COMMUNITY_ALL } from '@/api/community'

const props = defineProps<{
  // 是否是指定小区打开
  specified?: boolean
}>()
const emits = defineEmits(['refresh'])
// 表单引用
const formRef = ref()
// 弹窗显示状态
const visible = ref(false)
// 表单数据模型
const model = ref<Partial<ApiHouse.Item>>({})
// 是否为编辑模式
const isEdit = ref(false)
// 提交加载状态
const submitLoading = ref(false)
const id = ref()

const title = computed(() => (isEdit.value ? '编辑房源信息' : '新增房源信息'))
interface OpenOptions {
  record?: any
  isEditFlag?: boolean
  communityId?: string
}
const open = (options: OpenOptions = {}) => {
  const { record, isEditFlag = false, communityId } = options
  isEdit.value = isEditFlag
  id.value = isEditFlag ? record?.id : undefined
  if (isEditFlag) {
    // 编辑模式：从记录中提取表单字段
    model.value = {
      houseNumber: record?.houseNumber,
      houseNatureType: record?.houseNatureType,
      zhengbuCommunityTypeId: record?.zhengbuCommunityTypeId,
      houseExtraFiles: record?.houseExtraFiles ?? [],
      zhengbuCommunityId: props.specified ? communityId : record?.zhengbuCommunityId
    }
  } else {
    // 新增模式：重置表单字段
    model.value = {
      zhengbuCommunityId: props.specified ? communityId : undefined
    }
  }

  visible.value = true
}
const close = () => {
  visible.value = false
  formRef.value?.resetFields?.()
}

const submit = async () => {
  if (submitLoading.value) return
  submitLoading.value = true
  try {
    await formRef.value?.validate()
    const params: any = {
      ...model.value,
      id: id.value
    }
    await (isEdit.value ? API_HOUSE_EDIT : API_HOUSE_ADD)(params)
    emits('refresh')
    close()
  } catch (err) {
    handleFormError(err)
  }
  submitLoading.value = false
}
defineExpose({
  open
})
const setCommunityId = (value: string) => {
  model.value.zhengbuCommunityId = value
  Reflect.deleteProperty(model.value, 'zhengbuCommunityTypeId')
}
const dictStore = useDictStore()
const houseNatureTypeOptions = computed(() => dictStore.category.get('houseNatureType') || [])

const fetchHouseType = () => API_HOUSE_TYPE_ALL({ conditions: [genSimpleConditionItem('zhengbuCommunityId', model.value.zhengbuCommunityId)!] })
</script>
